import axios from "axios";
import authHeader from "utils/auth-header";
import {
  ActivePackages,
  ContentType,
  GenericResponse,
  PackageDetails,
  subscriptionsResponse,
  Transection,
} from "./types";

/**
 * @name getPackages
 * @description Get all the packages from the backend
 *
 * @returns all subscription packages
 */
const getPackages = async (): Promise<
  GenericResponse<subscriptionsResponse>
> => {
  try {
    const resp = await axios({
      method: "get",
      url: `${process.env.REACT_APP_BACKEND_SERVER}/v2/subscriptions`,
      headers: authHeader(),
    });
    return resp.data;
  } catch (err) {
    throw err;
  }
};

/**
 * @name getMiniPackagesDetailsById
 * @description Get the details of a mini package by its id
 *
 * @param id number
 * @returns Mini packages all details
 */
const getMiniPackagesDetailsById = async (
  id: number,
): Promise<GenericResponse<PackageDetails>> => {
  try {
    const resp = await axios({
      method: "get",
      url: `${process.env.REACT_APP_BACKEND_SERVER}/v2/subscriptions/info/${id}`,
      headers: authHeader(),
    });
    return resp.data;
  } catch (err) {
    throw err;
  }
};

/**
 * @name checkContentAccess
 * @description Check if the user has access to a content
 *
 * @param contentType string
 * @param id number
 * @returns
 */
const checkContentAccess = async (contentType: ContentType, id: number) => {
  try {
    const resp = await axios({
      method: "get",
      url: `${process.env.REACT_APP_BACKEND_SERVER}/v2/subscriptions/content/check-access/${contentType}/${id}`,
      headers: authHeader(),
    });
    return resp?.data;
  } catch (err) {
    throw err;
  }
};

/**
 * @name checkCreatorContentAccess
 * @description Check if the user has access to a content
 *
 * @param contentType string
 * @param id number
 * @returns
 */
const checkCreatorContentAccess = async (
  contentType: ContentType,
  id: number,
) => {
  try {
    const resp = await axios({
      method: "get",
      url: `${process.env.REACT_APP_BACKEND_SERVER}/v2/subscriptions/content/creator/check-access/${contentType}/${id}`,
      headers: authHeader(),
    });
    return resp?.data;
  } catch (err) {
    throw err;
  }
};

/**
 * @name checkContentAccessBySlug
 * @description Check if the user has access to a content by its slug
 *
 * @param slug string
 * @returns
 */
const checkContentAccessBySlug = async (slug: string) => {
  try {
    const resp = await axios({
      method: "get",
      url: `${process.env.REACT_APP_BACKEND_SERVER}/v2/subscriptions/content/check-access/${slug}`,
      headers: authHeader(),
    });
    return resp?.data;
  } catch (err) {
    throw err;
  }
};

/**
 * @name checkContentContentAccessBySlug
 * @description Check if the user has access to a content by its slug
 *
 * @param slug string
 * @returns
 */
const checkContentContentAccessBySlug = async (slug: string) => {
  try {
    const resp = await axios({
      method: "get",
      url: `${process.env.REACT_APP_BACKEND_SERVER}/v2/subscriptions/content/creator/check-access/${slug}`,
      headers: authHeader(),
    });
    return resp?.data;
  } catch (err) {
    throw err;
  }
};

/**
 * @name subscriptionCheckout
 * @description Create a checkout session for a subscription
 *
 * @param id number
 * @returns
 */
const subscriptionCheckout = async (id: number) => {
  try {
    const resp = await axios({
      method: "post",
      url: `${process.env.REACT_APP_BACKEND_SERVER}/v2/subscriptions/checkout/${id}`,
      headers: authHeader(),
    });
    return resp?.data;
  } catch (err) {
    throw err;
  }
};

/**
 * @name getSubscriptionTransection
 * @description Get all the transactions of the user
 *
 * @param page number
 * @param limit number
 * @returns
 */
const getSubscriptionTransection = async (
  page: number = 1,
  limit: number = 10,
): Promise<Array<Transection>> => {
  try {
    const resp = await axios({
      method: "get",
      url: `${process.env.REACT_APP_BACKEND_SERVER}/v2/subscriptions/transactions?page=${page}&limit=${limit}`,
      headers: authHeader(),
    });
    return resp?.data.data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * @name getAllPurchasedPackages
 * @description Get all the purchased packages of the user
 *
 * @returns
 */
const getAllPurchasedPackages = async (): Promise<Array<ActivePackages>> => {
  try {
    const resp = await axios({
      method: "get",
      url: `${process.env.REACT_APP_BACKEND_SERVER}/v2/subscriptions/purchased`,
      headers: authHeader(),
    });
    return resp?.data.data;
  } catch (err) {
    throw err;
  }
};
/**
 * @name cancleSubscriptionPackge
 * @description Cancel a subscription package`
 *
 * @param id number
 * @return
 */
const cancleSubscriptionPackge = async (id: number) => {
  try {
    const res = await axios({
      method: "post",
      url: `${process.env.REACT_APP_BACKEND_SERVER}/v2/subscriptions/cancel/${id}`,
      headers: authHeader(),
    });
    return res.data;
  } catch (err) {
    throw err;
  }
};

const getMiniPackagesDetailsBySlug = async (
  slug: string,
): Promise<GenericResponse<PackageDetails>> => {
  try {
    const resp = await axios({
      method: "get",
      url: `${process.env.REACT_APP_BACKEND_SERVER}/v2/subscriptions/info-slug/${slug}`,
      headers: authHeader(),
    });
    return resp.data;
  } catch (err) {
    throw err;
  }
};

const SubscriptionService = {
  getPackages,
  getMiniPackagesDetailsById,
  checkContentAccess,
  checkCreatorContentAccess,
  subscriptionCheckout,
  checkContentAccessBySlug,
  checkContentContentAccessBySlug,
  getSubscriptionTransection,
  getAllPurchasedPackages,
  cancleSubscriptionPackge,
  getMiniPackagesDetailsBySlug,
};

export default SubscriptionService;
